import { Box, Popover, Typography, colors, useI18nContext } from "@procore/core-react";
import React, { useCallback, useEffect, useState } from "react";
import { reportError } from "@/utils/logging-utils";
import {
   TopColumnResourceBreakdownDisplay,
   BottomColumnResourceBreakdownDisplay,
} from "./resource-breakdown-display";
import {
   formatTimeString,
   getDailyRateAndBurn,
   getDatesRangeString,
   getResourceBreakdownData,
} from "../utils";
import { getAttachedDate } from "@laborchart-modules/common/dist/datetime";
import { useColumnsCount } from "../hooks/use-columns-count";
import { useColumnToolbarUpdate } from "../hooks/use-column-toolbar-update";
import { tagRenderer } from "../task-card-content/tag-display";
import { useBoardsConfig } from "../hooks/use-boards-config";
import "./index.css";

const MAX_VISIBLE_TAGS = 2;

export default function ColumnToolbar(props: {
   columnRecord: any;
   location: string;
   taskBoardRef: React.RefObject<any>;
}) {
   const I18n = useI18nContext();
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   const [isLoaded, setIsLoaded] = useState(false);
   const taskBoardRef = props.taskBoardRef;

   const handleColumnsCountError = useCallback(
      (error: unknown) => {
         reportError(error as Error, "ColumnToolbar.useColumnsCount", {
            taskboard: {
               hasInstance: Boolean(taskBoardRef.current?.instance),
               hasColumnsStore: Boolean(taskBoardRef.current?.instance?.columns),
            },
         });
      },
      [taskBoardRef],
   );

   const columnsCount = useColumnsCount(taskBoardRef, handleColumnsCountError);
   const isSingleProjectView = columnsCount === 1;
   const project = props.columnRecord.originalData;
   useColumnToolbarUpdate(project.id);
   const {
      job_number: jobNumber,
      start_date: startDate,
      est_end_date: endDate,
      daily_start_time: startTime,
      daily_end_time: endTime,
      bid_rate: estAverageRate,
      tag_instances,
   } = project;

   const attachedEndDate = endDate ? getAttachedDate(endDate) : null;
   const attachedStartDate = startDate ? getAttachedDate(startDate) : null;
   let datesRangeString = "";
   if (attachedStartDate !== null) {
      datesRangeString = getDatesRangeString(attachedStartDate!, attachedEndDate, I18n);
   }

   const startTimeString = formatTimeString(startTime);
   const endTimeString = formatTimeString(endTime);

   const config = useBoardsConfig();

   useEffect(() => {
      taskBoardRef.current?.instance.project.on(
         "load",
         () => {
            setIsLoaded(true);
         },
         { once: true },
      );
   }, []);

   const resourceBreakdownData = getResourceBreakdownData(
      taskBoardRef.current?.instance.tasks?.filter(
         (task: any) => task.originalData.project_id === project.id,
      ) ?? [],
   );

   if (props.location === "top") {
      return (
         <div className="column-toolbar__top">
            {config.showProjectNumber && (
               <Typography
                  style={{
                     maxWidth: "100%",
                     textOverflow: "ellipsis",
                     overflow: "hidden",
                     whiteSpace: "nowrap",
                  }}
                  intent="small"
                  className="column-toolbar__top-item"
               >
                  {jobNumber || I18n.t("views.company.workforce_planning.boards.no_project_number")}
               </Typography>
            )}
            {config.showStartEndDate && (
               <Typography
                  style={{
                     maxWidth: "100%",
                     textOverflow: "ellipsis",
                     overflow: "hidden",
                     whiteSpace: "nowrap",
                  }}
                  intent="small"
                  className="column-toolbar__top-item"
               >
                  {datesRangeString}
               </Typography>
            )}
            {config.showStartEndTime && (
               <Typography
                  style={{
                     maxWidth: "100%",
                     textOverflow: "ellipsis",
                     overflow: "hidden",
                     whiteSpace: "nowrap",
                  }}
                  intent="small"
                  className="column-toolbar__top-item"
               >
                  {startTimeString} - {endTimeString}
               </Typography>
            )}
            {config.showProjectTags &&
               (tag_instances?.length > 0 ? (
                  <Box className="column-toolbar__tags">
                     {tagRenderer(tag_instances.slice(0, MAX_VISIBLE_TAGS))}
                     {tag_instances.length > MAX_VISIBLE_TAGS && (
                        <Popover
                           trigger="click"
                           placement="top"
                           overlay={
                              <Popover.Content>
                                 <div className="column-toolbar__tags-tooltip">
                                    {tagRenderer(tag_instances.slice(MAX_VISIBLE_TAGS))}
                                 </div>
                              </Popover.Content>
                           }
                        >
                           <span
                              className="column-toolbar__tags-more"
                              style={{ color: colors.gray40, background: colors.gray90 }}
                              onClick={(e) => e.stopPropagation()}
                           >
                              +{tag_instances.length - MAX_VISIBLE_TAGS}
                           </span>
                        </Popover>
                     )}
                  </Box>
               ) : (
                  <Typography intent="small" className="column-toolbar__no-tags">
                     {I18n.t("views.company.workforce_planning.boards.no_tags")}
                  </Typography>
               ))}
            {config.showResourceBreakdown && !isSingleProjectView && (
               <TopColumnResourceBreakdownDisplay
                  assignmentBreakdown={resourceBreakdownData.assignmentBreakdownArray}
                  requestBreakdown={resourceBreakdownData.requestBreakdownArray}
                  assignmentCount={resourceBreakdownData.assignmentCount}
                  requestCount={resourceBreakdownData.requestCount}
               />
            )}
         </div>
      );
   } else if (props.location === "bottom") {
      const dayFilter = new Date(
         taskBoardRef.current?.instance.project.transport.load.params.day_filter,
      );
      const assignments = taskBoardRef.current?.instance.tasks
         .filter((task: any) => task.originalData.project_id === project.id)
         .map((task: any) => task.originalData);
      const { dailyRate, dailyBurn } = getDailyRateAndBurn(
         project,
         assignments,
         taskBoardRef.current?.instance.company.paid_shift_hours,
         taskBoardRef.current?.instance.company.overtime_day_rates,
         dayFilter,
      );
      const dailyRateString = `${dailyRate.toLocaleString("en-US", {
         minimumFractionDigits: 0,
         maximumFractionDigits: 2,
      })}`;
      const dailyBurnString = `${dailyBurn.toLocaleString("en-US", {
         minimumFractionDigits: 0,
         maximumFractionDigits: 2,
      })}`;
      const estAverageRateString = estAverageRate
         ? `${estAverageRate.toLocaleString("en-US", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
           })}`
         : "0";

      const hasRateMetrics =
         config.showEstimatedAverageRate || config.showDailyRate || config.showDailyBurn;

      return (
         <div
            className={`column-toolbar__bottom${
               hasRateMetrics ? " column-toolbar__bottom--rate-metrics-visible" : ""
            }`}
         >
            {config.showEstimatedAverageRate && (
               <Typography intent="small" className="column-toolbar__row">
                  <span className="column-toolbar__label">Est. Avg. Rate</span>
                  <span className="column-toolbar__value">${estAverageRateString}/hr</span>
               </Typography>
            )}
            {config.showDailyRate && (
               <Typography intent="small" className="column-toolbar__row">
                  <span className="column-toolbar__label">Daily Rate</span>
                  <span className="column-toolbar__value">${dailyRateString}/hr</span>
               </Typography>
            )}
            {config.showDailyBurn && (
               <Typography intent="small" className="column-toolbar__row">
                  <span className="column-toolbar__label">Daily Burn</span>
                  <span className="column-toolbar__value">${dailyBurnString}/hr</span>
               </Typography>
            )}
            {config.showResourceBreakdown && isSingleProjectView && (
               <BottomColumnResourceBreakdownDisplay
                  assignmentBreakdown={resourceBreakdownData.assignmentBreakdownArray}
                  requestBreakdown={resourceBreakdownData.requestBreakdownArray}
                  assignmentCount={resourceBreakdownData.assignmentCount}
                  requestCount={resourceBreakdownData.requestCount}
               />
            )}
         </div>
      );
   }
}
